import { FC, ReactNode, useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import {
  Avatar,
  Badge,
  Box,
  Button,
  ButtonBase,
  Divider,
  IconButton,
  Skeleton,
  Typography,
} from '@mui/material'
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined'
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined'
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined'
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import {
  BLUE_COLORS_VARIANTS,
  PRIMARY_COLORS_VARIANTS,
  TEXT_DARK_COLORS_VARIANTS,
} from '@/utils/constants/colors'
import { isStarSportsPage } from '@/utils/starSportsUtils'
import { DrawerLinkElement } from 'components/shared/DrawerLinkElement'
import { DESKTOP_QUERY, HIDDEN_WITH_BOTTOM_NAV } from 'components/shared/bottomNavStyles'
import { HighlightedBoxElement } from 'components/shared/HighlightedBoxElement'
import { PartnerPageData } from 'types/types'
import { useAuth } from 'context/JWTContext'
import { useRouter } from 'next/router'
import { useQuery, useQueryClient } from 'react-query'
import { FlowTypes } from '@/lib/constants'
import { QUERY_KEYS } from '@/lib/cache-keys/queryKeys'
import { raffleApi } from 'api/raffleApi'
import { getCharityPath } from '@/utils/charitySlug'
import { getWalletPath } from '@/utils/walletPath'
import { useAffiliateMe } from 'hooks/useAffiliateMe'
import { useWalletBalance } from 'hooks/useWalletBalance'
import { TotalAvailableRow } from 'components/prize-draws/wallet/TotalAvailableRow'
import { ProfileDrawerLinksList } from './ProfileDrawerLinksList'
import { ProfileDrawerHighlightedItem } from './ProfileDrawerHighlightedItem'
import { ContactUsDrawer } from './ContactUsDrawer'
import { ChangeNicknameDrawer } from './ChangeNicknameDrawer'
import { CrossProductCard } from './CrossProductCard'
import { MainDrawer } from '../../shared/MainDrawer'

interface ProfileDrawerProps {
  pageTheme: PartnerPageData
  isPrizeDrawContext?: boolean
  renderTrigger?: (open: () => void, badgeCount: number) => ReactNode
}

export const ProfileDrawer: FC<ProfileDrawerProps> = ({
  pageTheme,
  isPrizeDrawContext,
  renderTrigger,
}) => {
  const { user, isAuthenticated, isInitialized, logout } = useAuth()
  const router = useRouter()
  const queryClient = useQueryClient()

  const { pathname } = router
  const isPrizeDrawView = isPrizeDrawContext ?? pathname.includes('/prize-draw')
  const hasCrossProductAccess = Boolean(pageTheme.feature_flags?.is_tbl_page)
  const isStarSports = isStarSportsPage(pageTheme)

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isContactUsOpen, setIsContactUsOpen] = useState(false)
  const [isChangeNicknameOpen, setIsChangeNicknameOpen] = useState(false)

  useEffect(() => {
    const close = () => setIsDrawerOpen(false)
    router.events.on('routeChangeComplete', close)
    return () => router.events.off('routeChangeComplete', close)
  }, [router.events])

  // Contact Us and Change Nickname open on their own, outside the profile
  // drawer, so they behave the same as the footer's Contact Us.
  const openFromMenu = (open: () => void) => {
    setIsDrawerOpen(false)
    open()
  }

  const correctLoginPath = pageTheme.is_external_login
    ? `/${pageTheme.company_domain}/auth/external-login`
    : `/${pageTheme.company_domain}/auth/email-check`

  const logoutRedirectUrl = isPrizeDrawView
    ? `/${pageTheme.company_domain}/prize-draw`
    : `/${pageTheme.company_domain}/landing`

  const {
    cash: walletBalance,
    siteCredit: siteCredits,
    total: totalBalance,
    isLoading: isBalanceLoading,
    isUnavailable: isBalanceUnavailable,
    format: formatBalance,
  } = useWalletBalance(isAuthenticated && hasCrossProductAccess)

  const { data: freeEntries } = useQuery({
    queryKey: [QUERY_KEYS.FREE_ENTRIES, pageTheme.id],
    queryFn: () => raffleApi.getFreeEntries(pageTheme.id),
    enabled: isAuthenticated && hasCrossProductAccess,
  })

  const { data: affiliate } = useAffiliateMe({
    enabled: isAuthenticated && hasCrossProductAccess,
  })

  const remainingFreeEntries = freeEntries?.available_count ?? 0

  const memoizedAvatarInitials = useMemo(() => {
    if (!user) return ''

    const nickname = user.nickname?.trim() ?? ''
    if (!nickname) return ''

    const [firstName, lastName] = nickname.split(' ')

    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
    }
    return `${nickname.charAt(0)}${nickname.charAt(0)}`.toUpperCase()
  }, [user])

  if (!isInitialized) return <Skeleton variant="circular" width={40} height={40} />

  if (!isAuthenticated || !user)
    return (
      <Link
        href={{
          pathname: correctLoginPath,
          query: {
            flowType: FlowTypes.AUTH,
            slug: pageTheme.company_domain,
            redirectTo: `/${pageTheme.company_domain}/prize-draw`,
          },
        }}
        passHref
        style={{ textDecoration: 'none' }}
      >
        <Button
          variant="contained"
          sx={{
            bgcolor: PRIMARY_COLORS_VARIANTS[500],
            color: TEXT_DARK_COLORS_VARIANTS[500],
            fontWeight: 650,
            fontSize: { xs: '0.875rem', md: '1rem', lg: '1.125rem' },
            px: { xs: '12px', md: 2 },
            py: 1,
            borderRadius: '10px',
            textTransform: 'none',
            boxShadow: '0 4px 12px rgba(255, 193, 7, 0.3)',
            '&:hover': {
              bgcolor: PRIMARY_COLORS_VARIANTS[500],
              filter: 'brightness(0.95)',
            },
            ...HIDDEN_WITH_BOTTOM_NAV,
          }}
        >
          Sign In
        </Button>
      </Link>
    )

  const isPredictorGame =
    pathname.startsWith('/[slug]/games') || pathname.startsWith('/[slug]/mini-league')
  const showDesktopBalance = hasCrossProductAccess && !renderTrigger && !isPredictorGame

  return (
    <>
      {renderTrigger?.(() => setIsDrawerOpen(true), isPrizeDrawView ? remainingFreeEntries : 0)}
      {showDesktopBalance && (
        // Opens the wallet page, like the mobile bottom nav's Balance.
        <ButtonBase
          href={getWalletPath(pageTheme.company_domain)}
          LinkComponent={Link}
          aria-label={
            isBalanceLoading || isBalanceUnavailable
              ? `Balance ${isBalanceLoading ? 'loading' : 'unavailable'}, open wallet`
              : `Balance ${formatBalance(totalBalance)}, open wallet`
          }
          sx={{
            // Mobile shows the balance in the bottom nav instead.
            display: 'none',
            [DESKTOP_QUERY]: { display: 'inline-flex' },
            mr: 1.5,
            px: 1.5,
            py: 1,
            gap: 0.5,
            borderRadius: '999px',
            bgcolor: '#0F1E27',
            border: '1px solid #1a2c38',
            whiteSpace: 'nowrap',
          }}
        >
          <Typography fontSize="0.875rem" color="#8fa0aa">
            Balance:
          </Typography>
          <Typography fontSize="0.875rem" fontWeight={800} color="#F0B040">
            {formatBalance(totalBalance)}
          </Typography>
        </ButtonBase>
      )}
      <MainDrawer
        open={isDrawerOpen}
        onOpen={() => setIsDrawerOpen(true)}
        onClose={() => setIsDrawerOpen(false)}
        trigger={
          renderTrigger ? undefined : (
            <Badge
              badgeContent={isPrizeDrawView ? remainingFreeEntries : 0}
              sx={{
                '& .MuiBadge-badge': {
                  bgcolor: BLUE_COLORS_VARIANTS[500],
                  border: `2px solid white`,
                  color: 'white',
                },
                ...HIDDEN_WITH_BOTTOM_NAV,
              }}
            >
              <IconButton
                aria-label="Open profile menu"
                sx={{
                  width: '46px',
                  height: '46px',
                  p: 0,
                  backgroundColor: isPrizeDrawView ? 'white' : 'transparent',
                  borderRadius: '100%',
                }}
              >
                <Avatar
                  sx={{
                    width: '46px',
                    height: '46px',
                    bgcolor: PRIMARY_COLORS_VARIANTS[500],
                    color: 'black',
                    fontSize: '15px',
                  }}
                >
                  {memoizedAvatarInitials}
                </Avatar>
              </IconButton>
            </Badge>
          )
        }
      >
        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" alignItems="center" gap={1} p={2} paddingBottom={0} paddingTop={4}>
            <Avatar sx={{ bgcolor: PRIMARY_COLORS_VARIANTS[500], p: 3 }}>
              {memoizedAvatarInitials}
            </Avatar>
            <Box display="flex" flexDirection="column">
              <Typography
                variant="body1"
                component="div"
                fontWeight={600}
                sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
              >
                {user.nickname}
              </Typography>
              <Typography
                variant="body1"
                component="div"
                fontWeight={300}
                sx={{ fontSize: { xs: '0.75rem', sm: '1rem' } }}
              >
                {user.email}
              </Typography>
            </Box>
          </Box>
          {hasCrossProductAccess && (
            <Box display="flex" flexDirection="column" p={2} gap={2}>
              <ProfileDrawerHighlightedItem
                icon={<AccountBalanceWalletOutlinedIcon fontSize="small" />}
                title="Cash Balance"
                description={formatBalance(walletBalance)}
                onRefresh={() => queryClient.invalidateQueries(['walletBalance'])}
                rightSlot={
                  <Link href={getWalletPath(pageTheme.company_domain)}>
                    <Button
                      sx={{
                        py: 1,
                        px: 1.5,
                        borderRadius: '10px',
                        fontWeight: 800,
                        textTransform: 'none',
                        width: 'fit-content',
                        bgcolor: PRIMARY_COLORS_VARIANTS[500],
                        color: TEXT_DARK_COLORS_VARIANTS[500],
                        boxShadow: 'none',
                        '&:hover': {
                          bgcolor: PRIMARY_COLORS_VARIANTS[300],
                        },
                      }}
                    >
                      Wallet
                    </Button>
                  </Link>
                }
              />
              <HighlightedBoxElement
                boxProps={{
                  sx: {
                    border: `2px solid ${PRIMARY_COLORS_VARIANTS[300]}`,
                    bgcolor: 'transparent !important',
                  },
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      display="flex"
                      alignItems="center"
                      p={0.5}
                      borderRadius="100%"
                      color={PRIMARY_COLORS_VARIANTS[650]}
                      bgcolor={PRIMARY_COLORS_VARIANTS[400]}
                      fontSize={24}
                    >
                      <PaymentsOutlinedIcon sx={{ fontSize: '16px' }} />
                    </Box>
                    <Typography
                      variant="body2"
                      component="div"
                      fontSize="14px"
                      fontWeight={500}
                      color={PRIMARY_COLORS_VARIANTS[500]}
                    >
                      SITE CREDIT
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    component="div"
                    color={PRIMARY_COLORS_VARIANTS[650]}
                    fontSize="14px"
                    fontWeight={900}
                  >
                    {formatBalance(siteCredits)}
                  </Typography>
                </Box>
              </HighlightedBoxElement>
              <TotalAvailableRow amount={formatBalance(totalBalance)} />
              <HighlightedBoxElement
                boxProps={{
                  sx: {
                    border: `2px solid ${BLUE_COLORS_VARIANTS[500]}`,
                    bgcolor: 'transparent !important',
                  },
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      display="flex"
                      alignItems="center"
                      p={0.5}
                      borderRadius="100%"
                      color={BLUE_COLORS_VARIANTS[500]}
                      bgcolor={BLUE_COLORS_VARIANTS[300]}
                      fontSize={24}
                    >
                      <ConfirmationNumberOutlinedIcon sx={{ fontSize: '16px' }} />
                    </Box>
                    <Typography
                      variant="body2"
                      component="div"
                      fontSize="14px"
                      fontWeight={500}
                      color={BLUE_COLORS_VARIANTS[500]}
                    >
                      FREE TICKETS
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    component="div"
                    fontSize="14px"
                    fontWeight={900}
                    color={BLUE_COLORS_VARIANTS[500]}
                  >
                    {remainingFreeEntries} available
                  </Typography>
                </Box>
              </HighlightedBoxElement>
            </Box>
          )}
          {hasCrossProductAccess && (
            <Box px={2}>
              <CrossProductCard
                pageTheme={pageTheme}
                target={isPrizeDrawView ? 'predictor' : 'prize-draws'}
              />
            </Box>
          )}
          {isPrizeDrawView && (
            <>
              <Divider />
              <ProfileDrawerLinksList pageTheme={pageTheme} />
            </>
          )}
          <Divider />
          <Box display="flex" flexDirection="column" p={1} gap={0.5}>
            {!isPrizeDrawView && pageTheme.feature_flags.sign_up_charity_page && (
              <DrawerLinkElement
                icon={<GroupsOutlinedIcon />}
                text={pageTheme.charity_page_breadcrumb_name || 'Partners'}
                link={getCharityPath(pageTheme)}
              />
            )}
            {isStarSports && (
              <DrawerLinkElement
                icon={<BorderColorOutlinedIcon />}
                text="Change Nickname"
                onClick={() => openFromMenu(() => setIsChangeNicknameOpen(true))}
              />
            )}
            {affiliate && (
              <DrawerLinkElement
                icon={<StorefrontOutlinedIcon />}
                text="TBL Partner Dashboard"
                link={`/${pageTheme.company_domain}/partner-account/${affiliate.id}`}
              />
            )}
            {!isStarSports && (
              <DrawerLinkElement
                icon={<PersonOutlineOutlinedIcon />}
                text="My Details"
                link={`/${pageTheme.company_domain}/my-details${
                  isPrizeDrawView ? '?from=prize-draw' : ''
                }`}
              />
            )}
            <DrawerLinkElement
              icon={<ChatBubbleOutlineOutlinedIcon />}
              text="Contact us"
              onClick={() => openFromMenu(() => setIsContactUsOpen(true))}
            />
          </Box>
          <Divider />
          <Box display="flex" flexDirection="column" p={1} gap={0.5}>
            <DrawerLinkElement
              icon={<LogoutOutlinedIcon />}
              text="Log Out"
              onClick={() => logout(logoutRedirectUrl)}
              isNegative
              hasChevron={false}
            />
          </Box>
        </Box>
      </MainDrawer>
      <ContactUsDrawer
        partnerId={pageTheme.id}
        defaultName={user.nickname ?? ''}
        defaultEmail={user.email ?? ''}
        trigger={null}
        open={isContactUsOpen}
        onClose={() => setIsContactUsOpen(false)}
        onOpen={() => setIsContactUsOpen(true)}
      />
      <ChangeNicknameDrawer
        currentNickname={user.nickname ?? ''}
        open={isChangeNicknameOpen}
        onClose={() => setIsChangeNicknameOpen(false)}
        onOpen={() => setIsChangeNicknameOpen(true)}
      />
    </>
  )
}
